import { useEffect, useRef, useState } from 'react'
import {
  animateNumber,
  DEFAULT_NUMBER_ANIMATION_DURATION_MS,
  formatAnimatedNumber
} from '../helpers/animatedNumberHelpers'

/**
 * Número animado reutilizable para contadores, KPIs, importes o porcentajes de cualquier módulo.
 * `value` acepta un número o texto numérico; los valores nulos o no finitos muestran «—».
 * `format` transforma el número en texto; por defecto redondea a entero y usa formato español.
 * `duration` indica la duración en milisegundos (650 por defecto).
 *
 * Anima desde el valor anterior y respeta la preferencia de movimiento reducido.
 * Los lectores de pantalla reciben el valor final sin los pasos intermedios.
 * Para animar valores fuera de React, usa animateNumber de helpers/animatedNumberHelpers.
 *
 * Ejemplos:
 * <AnimatedNumber value={total} />
 * <AnimatedNumber value={percentage} format={(value) => `${value.toFixed(1)}%`} />
 * <AnimatedNumber value={amount} format={formatMoney} duration={800} />
 */
export default function AnimatedNumber({
  value,
  format = formatAnimatedNumber,
  duration = DEFAULT_NUMBER_ANIMATION_DURATION_MS
}) {
  const numeric = value == null ? null : Number(value)
  const [display, setDisplay] = useState(numeric == null ? null : 0)
  const [origin, setOrigin] = useState(0)
  const previous = useRef(0)
  useEffect(() => {
    const from = previous.current ?? 0
    setOrigin(from)
    return animateNumber({
      from,
      to: numeric,
      duration,
      onUpdate: (currentValue) => {
        previous.current = currentValue
        setDisplay(currentValue)
      }
    })
  }, [numeric, duration])
  const final = numeric == null || !Number.isFinite(numeric) ? '—' : format(numeric)
  return (
    <span className="ubiqua-animated-number">
      <span className="visually-hidden">{final}</span>
      <span className="ubiqua-animated-number__size" aria-hidden="true">
        {final}
      </span>
      <span className="ubiqua-animated-number__size" aria-hidden="true">
        {format(origin)}
      </span>
      <span className="ubiqua-animated-number__value" aria-hidden="true">
        {display == null ? '—' : format(display)}
      </span>
    </span>
  )
}
